import { useEffect, useRef } from 'react';
import i18n from 'i18next';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';

const SUPPORTED = new Set(['en', 'cs', 'de', 'es', 'fr', 'it', 'ru', 'zh']);

function resolveLocale(raw: string): string {
  const code = raw.split('-')[0].toLowerCase();
  return SUPPORTED.has(code) ? code : 'en';
}

async function upsertLocale(userId: string, locale: string) {
  await supabase
    .from('profiles')
    .update({ preferred_locale: locale })
    .eq('user_id', userId);
}

// Syncs i18n.language → profiles.preferred_locale whenever the user changes language
// or when they first sign in. Fire-and-forget; errors are silent (non-critical).
export function useLocaleSync() {
  const { user } = useAuth();
  const lastSynced = useRef<string | null>(null);

  useEffect(() => {
    if (!user) {
      lastSynced.current = null;
      return;
    }

    const userId = user.id;
    const locale = resolveLocale(i18n.language);

    // Sync on mount / user change
    if (lastSynced.current !== locale) {
      lastSynced.current = locale;
      void upsertLocale(userId, locale);
    }

    // Sync on language switches while signed in
    const handleChange = (lng: string) => {
      const next = resolveLocale(lng);
      if (lastSynced.current !== next) {
        lastSynced.current = next;
        void upsertLocale(userId, next);
      }
    };

    i18n.on('languageChanged', handleChange);
    return () => { i18n.off('languageChanged', handleChange); };
  }, [user]);
}
